import { dequeue, listQueued, markAttempt, markSynced } from '../lib/offlineQueue';
import { uploadCaptureImage } from './storage';
import { runExtraction } from './extract';
import { setCaptureFailed, setExtraction } from './captures';
import { updateDoc } from 'firebase/firestore';
import { captureDoc } from './captures';

const MAX_ATTEMPTS = 5;
let draining = false;

/**
 * Upload queued photos oldest-first, then extract each.
 * Runs on reconnect and on demand. Never called on the capture path — capture
 * must never block on connectivity.
 */
export async function drainQueue(): Promise<{ uploaded: number; failed: number }> {
  if (draining || !navigator.onLine) return { uploaded: 0, failed: 0 };
  draining = true;
  let uploaded = 0;
  let failed = 0;

  try {
    const items = await listQueued();
    for (const item of items) {
      if (!navigator.onLine) break;
      try {
        const { storagePath, thumbnailPath, downloadUrl } = await uploadCaptureImage(
          item.uid,
          item.captureId,
          item.blob,
          item.thumbBlob,
        );
        await updateDoc(captureDoc(item.projectId, item.captureId), {
          storagePath,
          thumbnailPath,
          downloadUrl,
        });

        const result = await runExtraction({
          projectId: item.projectId,
          captureId: item.captureId,
          mode: 'photo',
          storagePath,
          instrument: item.instrument,
        });
        await setExtraction(item.projectId, item.captureId, result);

        await dequeue(item.captureId);
        uploaded++;
      } catch (e) {
        failed++;
        const message = (e as Error)?.message ?? 'Sync failed';
        await markAttempt(item.captureId, message);
        // Give up eventually rather than retrying a poisoned item forever —
        // but keep the capture itself, so the photo is never lost.
        if (item.attempts + 1 >= MAX_ATTEMPTS) {
          await setCaptureFailed(item.projectId, item.captureId, message);
          await dequeue(item.captureId);
        }
      }
    }
    if (uploaded > 0) markSynced();
  } finally {
    draining = false;
  }

  return { uploaded, failed };
}

/** Drain on reconnect and once at startup, in case the tab opens already online. */
export function startSyncWatcher() {
  const run = () => void drainQueue();
  window.addEventListener('online', run);
  if (navigator.onLine) run();
  return () => window.removeEventListener('online', run);
}
